import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import StaticHeader from './StaticHeader'
import ErrorBoundary from '../ErrorBoundary'
import { PageLoader } from '../loaders'
import OfflineNotice from '../OfflineNotice'
import PermissionNotice from '../PermissionNotice'
import CookiesNotice from '../CookiesNotice'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
import { connect } from 'react-redux'
import mapLoadingKeysToState from '../../utils/mapLoadingKeysToState'
const { isRP, isTP } = getProjectConditions()
import styled from '@emotion/styled'
import { Apartment } from '@entrata/portals-common/lib/icons'


// Get custom background image for Tenant Portal
const getCustomBackgroundImage = () => {
  if (!isTP) return null
  
  try {
    const tpUtils = require('portal-modules/tenant-portal-layout/utils/get-login-background')
    return tpUtils.getLoginBackgroundUrl()
  } catch (error) {
    console.error('Error loading custom background image:', error)
    return null
  }
}

const customBgImage = getCustomBackgroundImage()


const StyledContainer = styled('div')`
  display: flex;
  min-height: 100vh;
  ${({ theme }) => isTP ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  ` : ''}
`

const StyledLeftPanel = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  color: white;
  padding: 48px;
  
  ${({ hasCustomBg }) => hasCustomBg ? `
    background-image: url(${customBgImage});
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
  ` : ''}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
  }
  
  & > * {
    position: relative;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`

const StyledRightPanel = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => isTP ? theme.backgroundColor.light : 'white'};
  padding: 48px;
  
  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    min-height: 100vh;
  }
`

const StyledTitle = styled('h1')`
  font-size: 48px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 1024px) {
    font-size: 36px;
  }
`

const StyledMain = styled('main')`
  width: 100%;
  max-width: 400px;
  position: relative;
`

const StyledOriginalMain = styled('main')`
  border-radius: 8px, 8px, 0, 0;
  display: flex;
  height: 70vh;
  justify-content: center;
  overflow: auto;
  padding: 24px 32px;
  position: relative;

  @media only screen and (min-width: 768px) {
    padding: 48px 48px 80px;
  }

  ${({ theme }) => {
    return {
      backgroundColor: `${theme.mode.background_color}`,
    }
  }}
`

const StyledMobileHeader = styled('div')`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    text-align: center;
    margin-bottom: 48px;
    
    h1 {
      color: #333;
      font-size: 32px;
      font-weight: 600;
      margin: 0;
    }
  }
`
// Tenant Portal Login Panel styles start
export const StyledTPLoginPanel = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: white;
  padding: 48px;

  /* Apply background image if provided, otherwise use gradient */
  ${({ hasCustomBg, customBgImage }) => {
    if (hasCustomBg && customBgImage) {
      return `
        background-image: url(${customBgImage});
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
      `
    } else {
      return `
        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
      `
    }
  }};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ hasCustomBg }) =>
      hasCustomBg
        ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.8) 0%, rgba(139, 92, 246, 0.8) 50%, rgba(168, 85, 247, 0.8) 100%)'
        : 'rgba(0, 0, 0, 0.1)'};
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

export const StyledTPMainIcon = styled('div')`
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  svg {
    width: 40px;
    height: 40px;
    color: white;
  }
`

export const StyledTPTitle = styled('h1')`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 16px 0;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 1024px) {
    font-size: 36px;
  }
`

export const StyledTPSubtitle = styled('p')`
  font-size: 18px;
  font-weight: 400;
  margin: 0 0 48px 0;
  text-align: center;
  opacity: 0.9;
  max-width: 400px;
  line-height: 1.5;
`

export const StyledTPFeaturesContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 480px;
`

export const StyledTPFeatureCard = styled('div')`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }
`

export const StyledTPFeatureIcon = styled('div')`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`

export const StyledTPFeatureContent = styled('div')`
  flex: 1;
`

export const StyledTPFeatureTitle = styled('h3')`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: white;
`

export const StyledTPFeatureDescription = styled('p')`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  opacity: 0.9;
  line-height: 1.4;
`
// Tenant Portal Login Panel styles end

const StaticShell = ({ children, icon, iconSize, loading, logo }) => {
  // For Tenant Portal, use the new split-screen layout
  if (isTP) {

    return (
      <Fragment>
        <StyledContainer>
          <StyledTPLoginPanel hasCustomBg={!!customBgImage} customBgImage={customBgImage}>
            <StyledTPMainIcon>
              <Apartment />
            </StyledTPMainIcon>
            
            <StyledTPTitle>Tenant Portal</StyledTPTitle>
            
            <StyledTPSubtitle>
              Manage all your leasing needs in one place.
            </StyledTPSubtitle>
            
            <StyledTPFeaturesContainer>
              <StyledTPFeatureCard>
                <StyledTPFeatureIcon>
                  <Apartment />
                </StyledTPFeatureIcon>
                <StyledTPFeatureContent>
                  <StyledTPFeatureTitle>Simplified Management</StyledTPFeatureTitle>
                  <StyledTPFeatureDescription>
                    Access all your property information in one place
                  </StyledTPFeatureDescription>
                </StyledTPFeatureContent>
              </StyledTPFeatureCard>
              
              <StyledTPFeatureCard>
                <StyledTPFeatureIcon>
                  <Apartment />
                </StyledTPFeatureIcon>
                <StyledTPFeatureContent>
                  <StyledTPFeatureTitle>Multi-Property Support</StyledTPFeatureTitle>
                  <StyledTPFeatureDescription>
                    Manage all your locations from a single account
                  </StyledTPFeatureDescription>
                </StyledTPFeatureContent>
              </StyledTPFeatureCard>
            </StyledTPFeaturesContainer>
          </StyledTPLoginPanel>
          
          <StyledRightPanel>
            <StyledMain>
              <StyledMobileHeader>
                <h1>Tenant Portal</h1>
              </StyledMobileHeader>
              <ErrorBoundary>{children}</ErrorBoundary>
            </StyledMain>
          </StyledRightPanel>
        </StyledContainer>
        <OfflineNotice />
        <PermissionNotice />
        <PageLoader {...loading} />
      </Fragment>
    )
  }

  // For other portals, use the original layout
  return (
    <Fragment>
      <StaticHeader icon={icon} iconSize={iconSize} logo={logo} />
      <StyledOriginalMain>
        <ErrorBoundary>{children}</ErrorBoundary>
        <OfflineNotice />
        <PermissionNotice />
        {isRP ? <CookiesNotice /> : <></>}
      </StyledOriginalMain>
      <PageLoader {...loading} />
    </Fragment>
  )
}

StaticShell.propTypes = {
  /** Content for static shell */
  children: PropTypes.any,
  /** Icon to be shown in static header */
  icon: PropTypes.any.isRequired,
  /** Sice of the icon */
  iconSize: PropTypes.number,
  /** To show a loder inside shell when loading any content */
  loading: PropTypes.shape({
    message: PropTypes.string,
    show: PropTypes.bool,
    success: PropTypes.bool,
  }),
  /** Logo to be shown in static header */
  logo: PropTypes.any.isRequired,
}

export const loadingKeys = [
  'static',
  'login',
  'resetPassword',
  'enrollment',
  'createPassword',
]

export default connect(state =>
  mapLoadingKeysToState(state, { loadingKeys }),
)(StaticShell)
